// Centralised date/time formatters — Europe/London (BST in summer, GMT in winter).
// Backend always stores UTC; UI always RENDERS in UK local time.
// Use these helpers instead of bare `new Date(...).toLocaleString()`.

const UK_TZ = "Europe/London";
const LOCALE = "en-GB";

function safeDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Date + time in UK local — e.g. "28/06/2026, 14:09:34" */
export function fmtDateTime(value, opts = {}) {
  const d = safeDate(value);
  if (!d) return "—";
  return d.toLocaleString(LOCALE, { timeZone: UK_TZ, hour12: false, ...opts });
}

/** Date only — e.g. "28/06/2026" */
export function fmtDate(value) {
  const d = safeDate(value);
  if (!d) return "—";
  return d.toLocaleDateString(LOCALE, { timeZone: UK_TZ });
}

/** Time only — e.g. "14:09" */
export function fmtTime(value) {
  const d = safeDate(value);
  if (!d) return "—";
  return d.toLocaleTimeString(LOCALE, { timeZone: UK_TZ, hour12: false, hour: "2-digit", minute: "2-digit" });
}

/** Time with seconds — e.g. "14:09:34" */
export function fmtTimeSeconds(value) {
  const d = safeDate(value);
  if (!d) return "—";
  return d.toLocaleTimeString(LOCALE, { timeZone: UK_TZ, hour12: false });
}

/** Compact "YYYY-MM-DD HH:MM" form — for tables / audit log / mono columns */
export function fmtCompact(value) {
  const d = safeDate(value);
  if (!d) return "—";
  // Build using Intl.DateTimeFormat parts so we get the right tz-shifted YMD
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: UK_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

/** ISO-style YYYY-MM-DD in UK local — for date pickers, log keys */
export function fmtIsoDate(value) {
  const d = safeDate(value);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: UK_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Today's UK-local YYYY-MM-DD — used as default for daily-log date picker. */
export function todayUkIso() {
  return fmtIsoDate(new Date());
}
